// ©2024 Austin App House. All rights reserved.
//
// Converge Checkout.js vault-only card save for Payment Methods (no charge).
//
import React, { useState } from 'react';
import Notification from '../../notification';
import {
  buildCheckoutPaymentFields,
  confirmConvergeVaultCard,
  createConvergeVaultToken,
  waitForConvergeCheckout,
} from '../../../utils/convergePayments';

const onlyDigits = (value) => value.replace(/\D/g, '');

const formatCardNumber = (value) => onlyDigits(value)
  .slice(0, 19)
  .replace(/(.{4})/g, '$1 ')
  .trim();

const formatExpiry = (value) => {
  const digits = onlyDigits(value).slice(0, 4);
  if (digits.length < 3) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const initialCard = {
  cardholderName: '',
  cardNumber: '',
  expiry: '',
  cvv: '',
  zip: '',
};

function splitCardholderName(name) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  const parts = trimmed.split(/\s+/);
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' ') || parts[0],
  };
}

export default function ConvergeSaveCard({ onSuccess, user }) {
  const [card, setCard] = useState(initialCard);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field) => (e) => {
    const { value } = e.target;
    setCard((prev) => {
      if (field === 'cardNumber') return { ...prev, cardNumber: formatCardNumber(value) };
      if (field === 'expiry') return { ...prev, expiry: formatExpiry(value) };
      if (field === 'cvv') return { ...prev, cvv: onlyDigits(value).slice(0, 4) };
      if (field === 'zip') return { ...prev, zip: value.slice(0, 10) };
      return { ...prev, [field]: value };
    });
  };

  const validate = () => {
    if (!card.cardholderName.trim()) return 'Please enter the cardholder name';

    const digits = onlyDigits(card.cardNumber);
    if (digits.length < 13 || digits.length > 19) return 'Please enter a valid card number';
    if (!/^\d{2}\/\d{2}$/.test(card.expiry)) return 'Please enter a valid expiration date (MM/YY)';
    if (card.cvv.length < 3) return 'Please enter a valid CVV';
    return null;
  };

  const splitUserName = () => {
    const full = (user?.firstName || user?.lastName)
      ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
      : (user?.name || user?.email || '').trim();
    if (!full) return { firstName: 'Customer', lastName: '' };
    const parts = full.split(/\s+/);
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(' ') || parts[0],
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      Notification('error', { message: validationError });
      return;
    }

    setError(null);
    setLoading(true);

    const [expMonth, expYear] = card.expiry.split('/');
    const cardholder = splitCardholderName(card.cardholderName);
    const fallbackUser = splitUserName();

    try {
      const checkout = await waitForConvergeCheckout();
      const token = await createConvergeVaultToken({
        firstName: cardholder.firstName || fallbackUser.firstName,
        lastName: cardholder.lastName || fallbackUser.lastName,
      });

      const paymentFields = buildCheckoutPaymentFields({
        token,
        cardNumber: onlyDigits(card.cardNumber),
        expMonth,
        expYear,
        cvv: card.cvv,
        firstName: cardholder.firstName || fallbackUser.firstName,
        lastName: cardholder.lastName || fallbackUser.lastName,
        zip: card.zip,
      });

      if (!paymentFields.ssl_exp_date) {
        throw new Error('Please enter a valid expiration date (MM/YY)');
      }

      const callback = {
        onError: (err) => {
          const msg = typeof err === 'string' ? err : (err?.message || 'Checkout.js card save error');
          setError(msg);
          setLoading(false);
          Notification('error', { message: msg });
        },
        onCancelled: () => {
          setLoading(false);
          Notification('error', { message: 'Card save cancelled' });
        },
        onDeclined: (response) => {
          const msg = response?.ssl_result_message
            || response?.errorMessage
            || 'Card save declined';
          setError(msg);
          setLoading(false);
          Notification('error', { message: msg });
        },
        onApproval: async (response) => {
          try {
            const result = await confirmConvergeVaultCard({ transaction: response });
            if (result?.success === false) {
              throw new Error(result.message || result.error || 'Failed to save card');
            }
            Notification('success', {
              message: result?.message || 'Card saved for future payments.',
            });
            setCard(initialCard);
            setError(null);
            if (onSuccess) onSuccess();
          } catch (err) {
            const msg = err.response?.data?.error
              || err.response?.data?.message
              || err.message
              || 'Card tokenized but failed to save';
            setError(msg);
            Notification('error', { message: msg });
          } finally {
            setLoading(false);
          }
        },
      };

      checkout.pay(paymentFields, callback);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to start card save';
      setError(msg);
      setLoading(false);
      Notification('error', { message: msg });
    }
  };

  return (
    <form id="convergeSaveCardForm" onSubmit={handleSubmit}>
      <div className="field_col">
        <label className="field_name" htmlFor="vault-cardholder">
          Cardholder Name
        </label>
        <input
          id="vault-cardholder"
          className="dashboard-input"
          type="text"
          placeholder="Name as it appears on card"
          value={card.cardholderName}
          onChange={handleChange('cardholderName')}
          maxLength={80}
          autoComplete="cc-name"
          disabled={loading}
        />
      </div>

      <div className="field_col">
        <label className="field_name" htmlFor="vault-number">
          Card Number
        </label>
        <input
          id="vault-number"
          className="dashboard-input"
          type="text"
          inputMode="numeric"
          placeholder="1234 5678 9012 3456"
          value={card.cardNumber}
          onChange={handleChange('cardNumber')}
          autoComplete="cc-number"
          disabled={loading}
        />
      </div>

      <div className="field_col">
        <label className="field_name" htmlFor="vault-expiry">
          Expiration Date
        </label>
        <input
          id="vault-expiry"
          className="dashboard-input"
          type="text"
          inputMode="numeric"
          placeholder="MM/YY"
          value={card.expiry}
          onChange={handleChange('expiry')}
          maxLength={5}
          autoComplete="cc-exp"
          disabled={loading}
        />
      </div>

      <div className="field_col">
        <label className="field_name" htmlFor="vault-cvv">
          CVV
        </label>
        <input
          id="vault-cvv"
          className="dashboard-input"
          type="text"
          inputMode="numeric"
          placeholder="e.g. 123"
          value={card.cvv}
          onChange={handleChange('cvv')}
          maxLength={4}
          autoComplete="cc-csc"
          disabled={loading}
        />
      </div>

      <div className="field_col">
        <label className="field_name" htmlFor="vault-zip">
          Billing Zip
        </label>
        <input
          id="vault-zip"
          className="dashboard-input"
          type="text"
          inputMode="numeric"
          placeholder="Billing zip code"
          value={card.zip}
          onChange={handleChange('zip')}
          autoComplete="cc-postal-code"
          disabled={loading}
        />
      </div>

      {error && (
        <p style={{ color: '#d32f2f', margin: '4px 0', fontSize: '13px' }}>{error}</p>
      )}

      <div className="field_col" style={{ marginTop: '12px' }}>
        <button
          type="submit"
          className="dashboard-btn dashboard-btn--primary"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Card'}
        </button>
      </div>
    </form>
  );
}
